import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { FrontOverlayMode } from '../../../ips-react-front-fw/components/overlay';
import { fetchSubscriptionView } from './api';
import { SubscriptionOverlay } from './SubscriptionOverlay';
import type { SubscriptionOverlayMode } from './types';
import { registerDynamicBootstrap } from '../../shared/registerDynamicBootstrap';
import './subscription.css';

const mountSelector = '#ips-react-subscription-root';

const isOverlayMode = (value: string | undefined): value is FrontOverlayMode => value === 'drawer' || value === 'modal';

const normalizeMode = (value: string | undefined): SubscriptionOverlayMode => {
  if (value === 'page') return 'page';
  return isOverlayMode(value) ? value : 'drawer';
};

const normalizeSlug = (value: string | undefined, fallback: string): string => {
  const normalized = (value || '').replace(/^\/+|\/+$/g, '');
  return normalized || fallback;
};

const readOpenIdFromUrl = (queryKey: string): number | null => {
  if (!queryKey) return null;

  const raw = new URL(window.location.href).searchParams.get(queryKey);
  if (!raw) return null;

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const writeOpenIdToUrl = (queryKey: string, subscriptionId: number | null): void => {
  if (!queryKey) return;

  const url = new URL(window.location.href);
  if (subscriptionId && subscriptionId > 0) {
    url.searchParams.set(queryKey, String(subscriptionId));
  } else {
    url.searchParams.delete(queryKey);
  }

  const nextHref = `${url.pathname}${url.search}${url.hash}`;
  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextHref !== currentHref) {
    window.history.pushState({ ipsSubscriptionId: subscriptionId || null }, '', nextHref);
  }
};

const parseSubscriptionIdFromHref = (href: string, viewEndpoint: string): number | null => {
  let url: URL;

  try {
    url = new URL(href, window.location.origin);
  } catch (error) {
    return null;
  }

  const queryRaw = url.searchParams.get(viewEndpoint);
  if (queryRaw) {
    const queryId = Number.parseInt(queryRaw, 10);
    if (Number.isFinite(queryId) && queryId > 0) {
      return queryId;
    }
  }

  const escapedEndpoint = viewEndpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(?:^|/)${escapedEndpoint}/(\\d+)(?:/|$)`);
  const match = url.pathname.match(regex);
  if (!match || !match[1]) {
    return null;
  }

  const pathId = Number.parseInt(match[1], 10);
  return Number.isFinite(pathId) && pathId > 0 ? pathId : null;
};

const buildViewHref = (listUrl: string, viewEndpoint: string, subscriptionId: number): string => {
  try {
    const base = new URL(listUrl || window.location.href, window.location.origin);

    // Support non-pretty permalink mode (?view-subscription=123).
    if (base.search && !base.pathname.includes(`/${viewEndpoint}/`)) {
      base.searchParams.set(viewEndpoint, String(subscriptionId));
      return `${base.pathname}${base.search}${base.hash}`;
    }

    let pathname = base.pathname || '/';
    if (!pathname.endsWith('/')) {
      pathname += '/';
    }

    return `${base.origin}${pathname}${viewEndpoint}/${subscriptionId}/`;
  } catch (error) {
    return window.location.href;
  }
};

const bootstrap = () => {
  const mount = document.querySelector<HTMLElement>(mountSelector);
  if (!mount || mount.dataset.ipsReactMounted === '1') {
    return;
  }

  const endpoint = mount.dataset.endpoint || '';
  const nonce = mount.dataset.nonce || '';
  const mode = normalizeMode(mount.dataset.viewMode);
  const listUrl = mount.dataset.listUrl || window.location.href;
  const viewEndpoint = normalizeSlug(mount.dataset.viewEndpoint, 'view-subscription');
  const openQueryKey = mount.dataset.openQueryKey || 'ips_view_subscription';
  const dataOpenId = Number.parseInt(mount.dataset.openSubscriptionId || '', 10);

  if (!endpoint || !nonce) {
    return;
  }

  mount.dataset.ipsReactMounted = '1';

  const root = createRoot(mount);

  const App: React.FC = () => {
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [title, setTitle] = useState<string>('Subscription');
    const [content, setContent] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [openPageHref, setOpenPageHref] = useState<string>(listUrl);
    const activeSubscriptionId = useRef<number | null>(null);

    const closeOverlay = useCallback((syncUrl = true) => {
      setOpen(false);
      setLoading(false);
      setError(null);
      activeSubscriptionId.current = null;
      if (syncUrl) {
        writeOpenIdToUrl(openQueryKey, null);
      }
    }, []);

    const loadSubscription = useCallback(
      async (subscriptionId: number, href: string, syncUrl = true) => {
        if (mode === 'page') {
          window.location.href = href;
          return;
        }

        activeSubscriptionId.current = subscriptionId;
        setOpen(true);
        setLoading(true);
        setError(null);
        setTitle(`Subscription #${subscriptionId}`);
        setOpenPageHref(href);
        if (syncUrl) {
          writeOpenIdToUrl(openQueryKey, subscriptionId);
        }

        try {
          const payload = await fetchSubscriptionView(endpoint, nonce, subscriptionId);
          if (activeSubscriptionId.current !== subscriptionId) {
            return;
          }

          setTitle(payload.title || `Subscription #${subscriptionId}`);
          setContent(payload.html || '');
        } catch (requestError) {
          if (activeSubscriptionId.current !== subscriptionId) {
            return;
          }
          setError('Die Subscription konnte nicht geladen werden. Bitte versuche es erneut.');
        } finally {
          if (activeSubscriptionId.current === subscriptionId) {
            setLoading(false);
          }
        }
      },
      []
    );

    useEffect(() => {
      const initialId = readOpenIdFromUrl(openQueryKey) || (Number.isFinite(dataOpenId) && dataOpenId > 0 ? dataOpenId : null);
      if (!initialId || mode === 'page') {
        return;
      }

      loadSubscription(initialId, buildViewHref(listUrl, viewEndpoint, initialId), false);
    }, [loadSubscription]);

    useEffect(() => {
      if (mode === 'page') {
        return;
      }

      const handleClick = (event: Event) => {
        const anchor = (event.target as HTMLElement)?.closest<HTMLAnchorElement>('a[href]');
        if (!anchor) {
          return;
        }

        const withinWrapper = anchor.closest('.wooorder-subscription-wrapper');
        if (!withinWrapper) {
          return;
        }

        const href = anchor.getAttribute('href') || '';
        const subscriptionId = parseSubscriptionIdFromHref(href, viewEndpoint);
        if (!subscriptionId) {
          return;
        }

        event.preventDefault();
        loadSubscription(subscriptionId, anchor.href);
      };

      document.addEventListener('click', handleClick, true);
      return () => document.removeEventListener('click', handleClick, true);
    }, [loadSubscription]);

    useEffect(() => {
      if (mode === 'page') {
        return;
      }

      const onPopState = () => {
        const nextId = readOpenIdFromUrl(openQueryKey);
        if (nextId) {
          loadSubscription(nextId, buildViewHref(listUrl, viewEndpoint, nextId), false);
        } else {
          closeOverlay(false);
        }
      };

      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
    }, [closeOverlay, loadSubscription]);
    const overlayMode: FrontOverlayMode = mode === 'modal' ? 'modal' : 'drawer';

    if (mode === 'page') {
      return null;
    }

    return (
      <SubscriptionOverlay
        open={open}
        mode={overlayMode}
        loading={loading}
        title={title}
        html={content}
        error={error}
        openPageHref={openPageHref}
        onClose={() => closeOverlay(true)}
      />
    );
  };

  root.render(<App />);
};

registerDynamicBootstrap(bootstrap);
